import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { AVAILABILITY_META } from "../format";

// "Which sheet columns does the database actually have?" -- the catalog (static, from
// /columns) joined with live fill rates for the selected range (from /coverage).
// `fill` on the catalog entry is the 90-day prod baseline measured when the catalog was
// written; `live` is the same measure for whatever range is on screen.

const ORDER = ["AVAILABLE", "DERIVED", "PARTIAL", "MISSING"];

function FillBar({ value }) {
    if (value === null || value === undefined) {
        return <span className="text-[11px] text-gray-400">—</span>;
    }
    const pct = Math.round(value * 100);
    const tone = pct >= 90 ? "bg-elm-500" : pct >= 50 ? "bg-amber-400" : pct > 0 ? "bg-rose-400" : "bg-gray-200";
    return (
        <div className="flex items-center gap-2">
            <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-100">
                <div className={`h-full rounded-full ${tone}`} style={{ width: `${Math.max(pct, pct > 0 ? 3 : 0)}%` }} />
            </div>
            <span className="w-9 text-right text-[11px] tabular-nums text-gray-600">{pct}%</span>
        </div>
    );
}

FillBar.propTypes = { value: PropTypes.number };
FillBar.defaultProps = { value: null };

function SummaryCard({ availability, count, total, active, onClick }) {
    const meta = AVAILABILITY_META[availability];
    return (
        <button
            type="button"
            onClick={onClick}
            className={`flex flex-1 flex-col gap-1 rounded-2xl border bg-[white] p-4 text-left shadow-soft-xl transition ${
                active ? "border-elm-500 ring-1 ring-elm-500" : "border-transparent hover:border-gray-200"
            }`}
        >
            <span className="flex items-center gap-2 text-[12px] font-bold text-gray-700">
                <span className={`h-2.5 w-2.5 rounded-full ${meta.dot}`} />
                {meta.label}
            </span>
            <span className="text-2xl font-bold text-black">
                {count}
                <span className="ml-1 text-sm font-normal text-gray-400">/ {total}</span>
            </span>
        </button>
    );
}

SummaryCard.propTypes = {
    availability: PropTypes.string.isRequired,
    count: PropTypes.number.isRequired,
    total: PropTypes.number.isRequired,
    active: PropTypes.bool.isRequired,
    onClick: PropTypes.func.isRequired,
};

function CoveragePanel({ columns, coverage, isLoading }) {
    const [filter, setFilter] = useState(null);

    const liveByKey = useMemo(
        () => Object.fromEntries((coverage?.columns || []).map((c) => [c.key, c])),
        [coverage]
    );
    const counts = useMemo(
        () => ORDER.reduce((acc, a) => ({ ...acc, [a]: columns.filter((c) => c.availability === a).length }), {}),
        [columns]
    );
    const visible = filter ? columns.filter((c) => c.availability === filter) : columns;

    return (
        <section className="mt-3">
            <div className="flex flex-wrap gap-3">
                {ORDER.map((a) => (
                    <SummaryCard
                        key={a}
                        availability={a}
                        count={counts[a] || 0}
                        total={columns.length}
                        active={filter === a}
                        onClick={() => setFilter(filter === a ? null : a)}
                    />
                ))}
            </div>

            <p className="mt-3 text-[12px] text-gray-500">
                {isLoading && "Measuring fill rates for this range…"}
                {!isLoading && coverage && (
                    <>
                        Live fill rate measured over <b>{coverage.sampleSize.toLocaleString("en-IN")}</b> orders in the selected
                        range{coverage.truncated ? " (capped)" : ""}. Baseline is prod, last 90 days of dispatched orders. Patient /
                        contact / caregiver / sales-person are resolved from MyTatva and are not measured live.
                    </>
                )}
            </p>

            <div className="mt-2 overflow-x-auto rounded-2xl bg-[white] shadow-soft-xl">
                <table className="w-full min-w-[980px] font-sans">
                    <thead>
                        <tr className="border-b-2 border-gray-100 text-left text-[12px] text-gray-700">
                            <th className="px-4 py-3 font-bold">Sheet column</th>
                            <th className="px-3 py-3 font-bold">Status</th>
                            <th className="px-3 py-3 font-bold">Live fill</th>
                            <th className="px-3 py-3 font-bold">Baseline</th>
                            <th className="px-3 py-3 font-bold">Where it comes from</th>
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((col) => {
                            const meta = AVAILABILITY_META[col.availability] || AVAILABILITY_META.MISSING;
                            const live = liveByKey[col.key];
                            return (
                                <tr key={col.key} className="border-t border-gray-100 align-top text-[12px]">
                                    <td className="px-4 py-3">
                                        <div className="font-bold text-gray-900">{col.label}</div>
                                        <div className="text-[11px] text-gray-400">{col.group}</div>
                                        {col.duplicates?.length > 0 && (
                                            <div className="mt-0.5 text-[11px] text-gray-400">
                                                Also appears as: {col.duplicates.join(", ")}
                                            </div>
                                        )}
                                    </td>
                                    <td className="px-3 py-3">
                                        <span className={`inline-block whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-bold ${meta.chip}`}>
                                            {meta.label}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3">
                                        {live?.external ? (
                                            <span className="text-[11px] text-gray-400">MyTatva</span>
                                        ) : (
                                            <FillBar value={live?.fill ?? null} />
                                        )}
                                    </td>
                                    <td className="px-3 py-3"><FillBar value={col.fill ?? null} /></td>
                                    <td className="max-w-[460px] px-3 py-3">
                                        <div className="text-[11px] text-gray-700">{col.source || "—"}</div>
                                        {col.note && <div className="mt-1 text-[11px] leading-snug text-gray-500">{col.note}</div>}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </section>
    );
}

CoveragePanel.propTypes = {
    columns: PropTypes.arrayOf(PropTypes.object).isRequired,
    coverage: PropTypes.object,
    isLoading: PropTypes.bool,
};

CoveragePanel.defaultProps = { coverage: null, isLoading: false };

export default CoveragePanel;
